import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "@/hooks/useAppSelector.ts";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/api";
import {
  Popover,
  PopoverTrigger,
  Avatar,
  PopoverContent,
  Listbox,
  ListboxItem,
  Navbar,
  NavbarContent,
  NavbarMenuToggle,
  NavbarBrand,
  NavbarMenu,
  Divider,
  Chip,
} from "@heroui/react";
import { motion } from "framer-motion";
import {
  FaGear,
  FaDoorClosed,
  FaBuilding,
  FaHouse,
  FaMessage,
  FaMoneyBill,
  FaUsers,
  FaTelegram,
  FaBell,
  FaArrowDown,
  FaEllipsis,
  FaUser,
  FaLink,
  FaPrint,
  FaUsersGear,
  FaComments,
  FaIdCard,
  FaCartPlus,
  FaLock,
} from "react-icons/fa6";
import { FaBookmark } from "react-icons/fa";
import { HiArrowRight, HiArrowLeft } from "react-icons/hi2";
import { DarkModeToggler } from "../darkmode/darkModeToggler";
import { getPortal, hasAllPermissions } from "@/utils";
import { NotificationsList } from "../notifications/notifications";
import { ClientPermissions } from "@/api/auth/auth.ts";
import { FaMapMarkerAlt } from "react-icons/fa";
import { setChangeLogModal } from "@/app/store/user/reducer";
import { useAppDispatch } from "@/hooks/useAppDispatch";

interface PlainNavbarLink {
  path: string;
  icon: any;
  title: string;
  permissions?: number[];
}

interface DropdownNavbarLink extends PlainNavbarLink {
  children: PlainNavbarLink[];
}

type NavbarLink = PlainNavbarLink | DropdownNavbarLink;

function isDropdownNavbarLink(link: NavbarLink): link is DropdownNavbarLink {
  return (link as DropdownNavbarLink).children !== undefined;
}

export function VersionBadge(props: React.HTMLAttributes<HTMLDivElement>) {
  const state = useAppSelector((state) => state.user.modals.changeLog);
  const latestVersion = state.records[0]?.version;

  const dispatch = useAppDispatch();

  const onClick = useCallback(() => {
    dispatch(setChangeLogModal(true));
  }, [dispatch]);

  return (
    <div {...props} onClick={onClick}>
      <Chip color="default" variant="bordered" className="w-full">
        {latestVersion}
      </Chip>
    </div>
  );
}

export default function LayoutNavbar() {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const location = useAppSelector((state) => state.location.location);
  const [links, setLinks] = useState<NavbarLink[]>([]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed]);

  const desktopVariants = {
    collapsed: {
      width: 67,
    },
    expanded: {
      width: 288,
    },
  };

  useEffect(() => {
    if (location.id !== 0) {
      setLinks([
        {
          path: "/dashboard",
          icon: FaHouse,
          title: "Главная",
        },
        {
          path: "/dashboard/tickets",
          icon: FaMessage,
          title: "Тикеты",
        },
        {
          path: "/dashboard/meetingRooms",
          icon: FaComments,
          title: "Переговорки",
        },
        {
          icon: FaLink,
          title: "Коворкинг",
          children: [
            {
              path: "/dashboard/locations",
              icon: FaMapMarkerAlt,
              title: "Локации",
              permissions: [ClientPermissions.ViewRentedProperties],
            },
            {
              path: "/dashboard/offices",
              icon: FaDoorClosed,
              title: "Офисы",
              permissions: [ClientPermissions.ViewRentedProperties],
            },
            {
              path: "/dashboard/printers",
              icon: FaPrint,
              title: "Принтеры",
            },
            {
              path: "/dashboard/skud",
              icon: FaLock,
              title: "СКУД",
              permissions: [ClientPermissions.AccessLandlordLocks],
            },
          ],
          path: "#",
        },
        {
          icon: FaUsersGear,
          title: "Компания",
          children: [
            {
              path: "/dashboard/renters/" + location.id,
              icon: FaBuilding,
              title: "Моя компания",
              permissions: [],
            },
            {
              path: "/dashboard/users",
              icon: FaUsers,
              title: "Сотрудники",
              permissions: [ClientPermissions.ManageRenterEmployees],
            },
            {
              path: "/dashboard/requisites",
              icon: FaIdCard,
              title: "Реквизиты",
              permissions: [ClientPermissions.ViewRentalInvoices],
            },
            {
              path: "/dashboard/payments",
              icon: FaMoneyBill,
              title: "Платежи",
              permissions: [ClientPermissions.ViewRentalInvoices],
            },
          ],
          path: "#",
        },
        {
          path: "https://elasticspace.app",
          icon: FaCartPlus,
          title: "Маркетплейс",
        },
        {
          path: "/dashboard/likedOffices",
          icon: FaBookmark,
          title: "Понравившиеся офисы",
        },
      ]);
    } else {
      setLinks([
        {
          path: "/dashboard/selectLocation",
          icon: FaHouse,
          title: "Выбор компании",
        },
        {
          path: "https://elasticspace.app",
          icon: FaCartPlus,
          title: "Маркетплейс",
        },
      ]);
    }
  }, []);

  return (
    <>
      <motion.div
        className="hidden lg:flex flex-col p-4 justify-start gap-6 select-none h-screen rounded-none shadow-none max-h-screen bg-background dark:bg-zinc-900"
        initial={false}
        animate={collapsed ? "collapsed" : "expanded"}
        variants={desktopVariants}>
        <div className="flex flex-row items-center gap-4">
          <a href="#" className="p-1 hidden lg:block" onClick={toggleCollapsed}>
            {collapsed ? (
              <HiArrowRight className="text-xl" />
            ) : (
              <HiArrowLeft className="text-xl" />
            )}
          </a>

          <motion.div animate={{ opacity: collapsed ? 0 : 1 }}>
            <a href="/dashboard/">
              <img
                className="logo h-16"
                src="/logo.svg"
                alt="Elastic Management"
              />
            </a>
          </motion.div>
        </div>

        {!collapsed && (
          <div className="flex flex-col gap-2">
            <VersionBadge />
            {location.id !== 0 && (
              <div className="flex flex-row items-center justify-between w-full">
                <div className="flex flex-col">
                  <span className="font-semibold text-sm text-default-500">
                    Компания:
                  </span>
                  <span className="font-semibold text-sm">
                    {location.display_name}
                  </span>
                </div>
                <Link
                  to="/dashboard/selectLocation"
                  className="font-semibold text-sm text-primary">
                  Сменить
                </Link>
              </div>
            )}
            {location.id == 0 && (
              <div className="flex flex-row items-center justify-between w-full">
                <span className="font-semibold text-sm">
                  Компания не выбрана
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-2 flex-grow overflow-y-auto overflow-x-clip">
          {links.map((link) => (
            <>
              {!collapsed && isDropdownNavbarLink(link) && (
                <DropdownNavLink link={link} collapsed={collapsed} />
              )}

              {!isDropdownNavbarLink(link) && (
                <NavLink link={link} collapsed={collapsed} />
              )}
            </>
          ))}
        </div>

        <div className="flex flex-col justify-end gap-4">
          <DarkModeToggler collapsed={collapsed} />
          <Notifications collapsed={collapsed} />
          <Divider />
          <NavUser collapsed={collapsed} />
        </div>
      </motion.div>
      <MobileNav links={links} />
    </>
  );
}

function Notifications({ collapsed }: { collapsed: boolean }) {
  const unreadCount = useAppSelector(
    (state) => state.notifications.unreadCount,
  );

  return (
    <Popover
      showArrow={false}
      placement="top"
      portalContainer={document.getElementById("main")!}
      backdrop="blur">
      <PopoverTrigger className="shadow-none border-none bg-none outline-none">
        <button
          className={
            `flex flex-row items-center gap-4 py-2 rounded-md text-foreground-600 bg-default bg-opacity-0 hover:bg-opacity-30 transition-all ` +
            `${collapsed ? "hover:bg-opacity-0" : "px-2"}`
          }>
          <div
            className={`flex flex-col items-center justify-center min-w-8 min-h-8 rounded-lg bg-default bg-opacity-30`}>
            <FaBell className="text-medium top-2 relative" />
            <span
              className={`top-2 left-4 w-4 h-4 relative z-10 rounded-full text-[10px] 
              ${unreadCount > 0 ? "bg-danger animate-pulse" : "bg-default"}`}>
              {unreadCount}
            </span>
          </div>
          <motion.div animate={{ opacity: collapsed ? 0 : 1 }}>
            <span className="text-sm font-semibold">Уведомления</span>
          </motion.div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="min-w-96 max-w-96">
        <NotificationsList />
      </PopoverContent>
    </Popover>
  );
}

function MobileNav({ links }: { links: NavbarLink[] }) {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const loc = useLocation();
  const location = useAppSelector((state) => state.location.location);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [loc.pathname]);

  const onClick = useCallback(() => {
    setIsMenuOpen(false);
  }, [setIsMenuOpen]);

  return (
    <Navbar
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
      className="lg:hidden z-[99999]">
      <NavbarContent>
        <NavbarMenuToggle
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        />

        <NavbarBrand>
          <img className="logo h-12" src="/logo.svg" alt="Elastic Management" />
        </NavbarBrand>

        <VersionBadge />

        <NavbarMenu className="p-4 z-[99999]" portalContainer={getPortal()}>
          <>
            {location.id !== 0 && (
              <div className="flex flex-row items-center justify-between w-full">
                <div className="flex flex-col">
                  <span className="font-semibold text-sm text-default-500">
                    Компания:
                  </span>
                  <span className="font-semibold text-sm">
                    {location.display_name}
                  </span>
                </div>
                <Link
                  to="/dashboard/selectLocation"
                  className="font-semibold text-sm text-primary">
                  Сменить
                </Link>
              </div>
            )}
            {location.id == 0 && (
              <div className="flex flex-row items-center justify-between w-full">
                <span className="font-semibold text-sm">
                  Компания не выбрана
                </span>
              </div>
            )}
            {links.map((link) => (
              <MobileNavLink
                link={link}
                key={link.path + "m"}
                collapsed={false}
                onClick={onClick}
              />
            ))}
            <div className="flex-grow" />
            <div className="mb-40">
              <NavUser collapsed={false} />
            </div>
          </>
        </NavbarMenu>
      </NavbarContent>
    </Navbar>
  );
}

function DropdownNavLink({
  link,
  collapsed,
}: {
  link: DropdownNavbarLink;
  collapsed: boolean;
}) {
  const [localCollapsed, setLocalCollapsed] = useState<boolean>(true);

  const toggleLocalCollapseState = useCallback(() => {
    setLocalCollapsed((prev) => !prev);
  }, []);

  return link.children.length > 0 ? (
    <div className="flex flex-col cursor-pointer">
      <div
        className={
          `flex flex-row items-center gap-4 py-2 rounded-md text-foreground-600 bg-default bg-opacity-0 hover:bg-opacity-30 transition-all ` +
          `${collapsed ? "hover:bg-opacity-0" : "px-2"}`
        }
        onClick={toggleLocalCollapseState}>
        <motion.div
          className="flex flex-row items-center justify-between w-full"
          animate={{ opacity: collapsed ? 0 : 1 }}>
          <div className="flex flex-row gap-4 items-center">
            <div
              className={`flex flex-col items-center justify-center min-w-8 min-h-8 rounded-lg bg-default bg-opacity-30 text-default-500`}>
              <FaEllipsis />
            </div>
            <span className="text-sm font-semibold text-nowrap">
              {link.title}
            </span>
          </div>
          <motion.div
            animate={{ rotate: localCollapsed ? 0 : 180 }}
            transition={{ duration: 0.2 }}>
            <FaArrowDown className="text-sm" />
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={false}
        animate={{
          opacity: localCollapsed ? 0 : 1,
          maxHeight: localCollapsed ? 0 : "1000px",
        }}
        transition={{ duration: 0.2 }}
        className={`flex flex-col gap-2 overflow-hidden ${localCollapsed ? "" : "px-2 pt-2"} transition-all`}>
        {link.children.map((children) => (
          <NavLink link={children} key={children.path} collapsed={collapsed} />
        ))}
      </motion.div>
    </div>
  ) : null;
}

function NavLink({
  link,
  collapsed,
}: {
  link: NavbarLink;
  collapsed: boolean;
}) {
  const userPermissions = useAppSelector((state) => state.user.permissions);

  const permissionsCheck = link.permissions
    ? hasAllPermissions(link.permissions, userPermissions.scopes)
    : true;

  return permissionsCheck ? (
    isDropdownNavbarLink(link) ? (
      link.children.map((children) => (
        <NavLink link={children} key={children.path} collapsed={collapsed} />
      ))
    ) : (
      <Link
        to={link.path}
        id={link.title === "Маркетплейс" ? "marketplace-link" : undefined}
        className={
          `flex flex-row items-center gap-4 py-2 rounded-md text-foreground-600 bg-default bg-opacity-0 hover:bg-opacity-30 transition-all ` +
          `${collapsed ? "hover:bg-opacity-0" : "px-2"}`
        }>
        <div
          className={`flex flex-col items-center justify-center min-w-8 min-h-8 rounded-lg ${link.title == "Тикеты" ? "bg-primary text-primary-500" : "bg-default text-default-500"} bg-opacity-30`}>
          <link.icon className="text-medium" />
        </div>
        <motion.div animate={{ opacity: collapsed ? 0 : 1 }}>
          <span className="text-sm font-semibold text-nowrap">
            {link.title}
          </span>
        </motion.div>
      </Link>
    )
  ) : null;
}

function MobileNavLink({
  link,
  collapsed,
  onClick,
}: {
  link: NavbarLink;
  collapsed: boolean;
  onClick: (e: any) => void;
}) {
  const userPermissions = useAppSelector((state) => state.user.permissions);

  const permissionsCheck = link.permissions
    ? hasAllPermissions(link.permissions, userPermissions.scopes)
    : true;

  return permissionsCheck ? (
    isDropdownNavbarLink(link) ? (
      link.children.map((children) => (
        <NavLink link={children} key={children.path} collapsed={collapsed} />
      ))
    ) : (
      <Link
        to={link.path}
        id={
          link.title === "Маркетплейс" ? "marketplace-link-mobile" : undefined
        }
        className={
          `flex flex-row items-center gap-4 py-2 rounded-md text-foreground-600 bg-default bg-opacity-0 hover:bg-opacity-30 transition-all ` +
          `${collapsed ? "hover:bg-opacity-0" : "px-2"}`
        }
        onClick={onClick}>
        <div
          className={`flex flex-col items-center justify-center min-w-8 min-h-8 rounded-lg bg-default bg-opacity-30 text-default-500`}>
          <link.icon className="text-medium" />
        </div>
        <motion.div animate={{ opacity: collapsed ? 0 : 1 }}>
          <span className="text-sm font-semibold text-nowrap">
            {link.title}
          </span>
        </motion.div>
      </Link>
    )
  ) : null;
}

function NavUser({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user.currentUser);

  const onUserAction = (key: any) => {
    switch (key) {
      case "telegram":
        window.location.href = "https://t.me/App_elasticspacebot";
        break;
      case "profile":
        navigate("/dashboard/me");
        break;
      case "settings":
        navigate("/dashboard/settings");
        break;
      case "exit":
        api.auth.logout();
        break;
      default:
        break;
    }
  };

  return (
    <Popover
      showArrow={false}
      placement="top"
      portalContainer={document.getElementById("main")!}>
      <PopoverTrigger className="shadow-none border-none bg-none outline-none">
        <button className="inline-flex items-center gap-2 rounded-small outline-none data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2 z-10 subpixel-antialiased transition-transform">
          <div>
            <Avatar size="sm" name={`${user.first_name?.split("")[0]}`} />
          </div>
          <motion.div
            animate={{ opacity: collapsed ? 0 : 1 }}
            className="flex flex-col gap-1">
            <span className="text-sm text-nowrap">{`${user.first_name} ${user.last_name}`}</span>
          </motion.div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-1 min-w-48">
        <Listbox aria-label="Действия" onAction={onUserAction}>
          <ListboxItem startContent={<FaTelegram />} key="telegram">
            Telegram
          </ListboxItem>
          <ListboxItem startContent={<FaUser />} key="profile">
            Мой профиль
          </ListboxItem>
          <ListboxItem startContent={<FaGear />} key="settings">
            Настройки
          </ListboxItem>
          <ListboxItem
            startContent={<FaDoorClosed />}
            key="exit"
            className="text-danger"
            color="danger">
            Выйти
          </ListboxItem>
        </Listbox>
      </PopoverContent>
    </Popover>
  );
}
